import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Loader, FileText, X, ArrowRight, CornerDownLeft, Clock,
  Building2, Users, Bell, Newspaper, Calendar, BookOpen, Globe, Phone,
} from 'lucide-react';
import { searchStaticPages } from '../../data/sitePages';
import { DB_CATEGORY_CONFIG, getResultLink, searchDB, highlight } from './SiteSearch';
import { toEnPath } from '../../utils/locale';

const BRAND = '#7B0D1E';

const TXT = {
  tr: {
    dialog: 'Sitede ara', placeholder: 'Bölüm, kişi, duyuru, haber ara…', clear: 'Temizle', close: 'Kapat',
    recent: 'Son aramalar', quick: 'Hızlı erişim', searching: 'Aranıyor…', noResults: 'Sonuç bulunamadı',
    noMatch: q => `“${q}” için eşleşme yok. Farklı bir kelime deneyin.`, pages: 'Sayfalar',
    nav: 'gezin', open: 'aç', results: 'sonuç', footer: 'Hacettepe Üniversitesi Fen Fakültesi',
  },
  en: {
    dialog: 'Search the site', placeholder: 'Search departments, people, announcements, news…', clear: 'Clear', close: 'Close',
    recent: 'Recent searches', quick: 'Quick access', searching: 'Searching…', noResults: 'No results found',
    noMatch: q => `No matches for “${q}”. Try a different word.`, pages: 'Pages',
    nav: 'navigate', open: 'open', results: 'results', footer: 'Hacettepe University Faculty of Science',
  },
};
const RECENT_KEY = 'ff-recent-searches';

const QUICK_LINKS = [
  { title: 'Lisans Programları',  en: 'Undergraduate Programs', url: '/programlar/lisans',     icon: Building2, color: '#7B0D1E' },
  { title: 'Akademik Personel',   en: 'Faculty Members',        url: '/fakulte-personeli/ogretim-uyeleri', icon: Users,     color: '#0f766e' },
  { title: 'Duyurular',           en: 'Announcements',          url: '/duyurular',             icon: Bell,      color: '#1d4ed8' },
  { title: 'Haberler',            en: 'News',                   url: '/haberler',              icon: Newspaper, color: '#b45309' },
  { title: 'Etkinlikler',         en: 'Events',                 url: '/etkinlikler',           icon: Calendar,  color: '#059669' },
  { title: 'Öğrenci Toplulukları', en: 'Student Clubs',         url: '/ogrenci-topluluklari', icon: BookOpen,  color: '#7c3aed' },
  { title: 'Erasmus',             en: 'Erasmus+',               url: '/programlar/erasmus',    icon: Globe,     color: '#0369a1' },
  { title: 'İletişim',            en: 'Contact',                url: '/iletisim',              icon: Phone,     color: '#be185d' },
];

function readRecent() {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch { return []; }
}
function saveRecent(q) {
  try {
    const list = [q, ...readRecent().filter(x => x.toLowerCase() !== q.toLowerCase())].slice(0, 5);
    localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  } catch { /* depolama kapalı olabilir */ }
}

// Bölüm sayfaları dış sitelere gider
const pageLink = p => (p.url.startsWith('http') ? { external: p.url } : { internal: p.url });

function Kbd({ children }) {
  return (
    <kbd style={{
      fontFamily: 'inherit', fontSize: 11, fontWeight: 600, color: '#6b7280',
      background: '#f3f4f6', border: '1px solid #e5e7eb', borderBottomWidth: 2,
      borderRadius: 6, padding: '1px 6px', minWidth: 20, textAlign: 'center', display: 'inline-block',
    }}>{children}</kbd>
  );
}

export default function SearchModal({ open, onClose }) {
  const [query, setQuery]     = useState('');
  const [dbResults, setDB]    = useState({});
  const [pages, setPages]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive]   = useState(0);
  const [recent, setRecent]   = useState([]);
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isEn = (i18n.language || '').startsWith('en');
  const L = TXT[isEn ? 'en' : 'tr'];
  const inputRef = useRef(null);
  const listRef  = useRef(null);
  const debounce = useRef(null);
  const reqId    = useRef(0);
  const isEnRef  = useRef(isEn);
  isEnRef.current = isEn;

  // Açılışta sıfırla, odakla, sayfa kaydırmayı kilitle
  useEffect(() => {
    if (!open) return;
    setQuery(''); setDB({}); setPages([]); setActive(0); setRecent(readRecent());
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { clearTimeout(t); document.body.style.overflow = prev; };
  }, [open]);

  const runSearch = useCallback(async (q) => {
    const id = ++reqId.current;
    if (!q.trim()) { setDB({}); setPages([]); setLoading(false); return; }
    setLoading(true);
    const [dbData, staticPages] = await Promise.all([searchDB(q), Promise.resolve(searchStaticPages(q, isEnRef.current))]);
    if (id !== reqId.current) return; // eski isteğin sonucu
    setDB(dbData); setPages(staticPages); setActive(0); setLoading(false);
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => runSearch(val), 250);
  };

  const go = useCallback((link) => {
    if (query.trim()) saveRecent(query.trim());
    onClose();
    if (!link) return;
    if (link.external) window.open(link.external, '_blank', 'noopener');
    // İngilizce sitede iç bağlantılar /en altında açılır
    else navigate(isEn ? toEnPath(link.internal) : link.internal);
  }, [query, navigate, onClose, isEn]);

  // Klavye ile gezinmek için düz liste
  const flat = useMemo(() => {
    if (!query.trim()) return QUICK_LINKS.map(q => ({ key: q.url, select: () => go({ internal: q.url }) }));
    const out = [];
    Object.keys(DB_CATEGORY_CONFIG).forEach(type => {
      (dbResults[type] || []).forEach(item => out.push({ key: `${type}-${item.id}`, select: () => go(getResultLink(type, item)) }));
    });
    pages.forEach(p => out.push({ key: `p-${p.url}`, select: () => go(pageLink(p)) }));
    return out;
  }, [query, dbResults, pages, go]);

  const indexOf = (key) => flat.findIndex(f => f.key === key);

  useEffect(() => {
    listRef.current?.querySelector(`[data-idx="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a + 1, flat.length - 1)); }
    else if (e.key === 'ArrowUp')   { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter')     { e.preventDefault(); flat[active]?.select(); }
  };

  const total  = flat.length;
  const hasQ   = query.trim().length > 0;

  const rowStyle = (isActive) => ({
    width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 12,
    padding: '10px 12px', borderRadius: 12, border: 'none', cursor: 'pointer',
    background: isActive ? 'rgba(123,13,30,0.06)' : 'transparent',
    boxShadow: isActive ? `inset 3px 0 0 ${BRAND}` : 'none',
    transition: 'background 0.12s',
  });

  const iconTile = (Icon, color) => (
    <span style={{
      width: 36, height: 36, borderRadius: 10, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: `${color}14`, color,
    }}>
      <Icon size={17} />
    </span>
  );

  const sectionTitle = (label, color = '#9ca3af') => (
    <div style={{ padding: '14px 12px 6px', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color }}>
      {label}
    </div>
  );

  const content = (
    <AnimatePresence>
      {open && (
        <motion.div
          key="search-overlay"
          className="site-search-overlay"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
          style={{
            position: 'fixed', inset: 0, zIndex: 10000,
            background: 'rgba(17, 10, 12, 0.55)',
            backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
            display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
            padding: '12vh 16px 16px',
          }}
        >
          <motion.div
            role="dialog" aria-modal="true" aria-label={L.dialog}
            initial={{ opacity: 0, y: -16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.98 }}
            transition={{ duration: 0.22, ease: [0.25, 0.46, 0.45, 0.94] }}
            onKeyDown={onKeyDown}
            style={{
              width: '100%', maxWidth: 680, maxHeight: '76vh',
              background: '#fff', borderRadius: 20, overflow: 'hidden',
              boxShadow: '0 30px 80px rgba(0,0,0,0.35), 0 0 0 1px rgba(0,0,0,0.04)',
              display: 'flex', flexDirection: 'column',
            }}
          >
            {/* Üst marka şeridi */}
            <div style={{ height: 4, background: `linear-gradient(90deg, ${BRAND}, #b3122d, ${BRAND})`, flexShrink: 0 }} />

            {/* Arama girişi */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '18px 20px', borderBottom: '1px solid #f1f1f3', flexShrink: 0 }}>
              {loading
                ? <Loader size={22} color={BRAND} className="animate-spin" style={{ flexShrink: 0 }} />
                : <Search size={22} color={BRAND} style={{ flexShrink: 0 }} />}
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={handleChange}
                placeholder={L.placeholder}
                autoComplete="off"
                spellCheck={false}
                style={{
                  flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent',
                  fontSize: 18, fontWeight: 500, color: '#111827', fontFamily: 'inherit',
                }}
              />
              {query && (
                <button type="button" onClick={() => { setQuery(''); runSearch(''); inputRef.current?.focus(); }}
                  title={L.clear}
                  style={{ border: 'none', background: '#f3f4f6', color: '#6b7280', borderRadius: 999, width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
                  <X size={14} />
                </button>
              )}
              <button type="button" onClick={onClose} title={L.close}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, flexShrink: 0 }}>
                <Kbd>ESC</Kbd>
              </button>
            </div>

            {/* Gövde */}
            <div ref={listRef} style={{ overflowY: 'auto', padding: '4px 8px 8px', flex: 1 }}>
              {!hasQ ? (
                <>
                  {recent.length > 0 && (
                    <>
                      {sectionTitle(L.recent)}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '0 12px 4px' }}>
                        {recent.map(r => (
                          <button key={r} type="button"
                            onClick={() => { setQuery(r); runSearch(r); inputRef.current?.focus(); }}
                            className="site-search-chip"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 999, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, color: '#374151', cursor: 'pointer' }}>
                            <Clock size={12} color="#9ca3af" /> {r}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                  {sectionTitle(L.quick)}
                  <div className="site-search-quick" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 4 }}>
                    {QUICK_LINKS.map((q, i) => (
                      <button key={q.url} type="button" data-idx={i}
                        onMouseEnter={() => setActive(i)}
                        onClick={() => go({ internal: q.url })}
                        style={rowStyle(active === i)}>
                        {iconTile(q.icon, q.color)}
                        <span style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', flex: 1 }}>{isEn ? q.en : q.title}</span>
                        <ArrowRight size={14} color={active === i ? BRAND : '#d1d5db'} />
                      </button>
                    ))}
                  </div>
                </>
              ) : loading && total === 0 ? (
                <div style={{ padding: '40px 0', textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
                  <Loader size={22} className="animate-spin" style={{ margin: '0 auto 10px', color: BRAND }} />
                  {L.searching}
                </div>
              ) : total === 0 ? (
                <div style={{ padding: '44px 20px', textAlign: 'center' }}>
                  <div style={{ width: 56, height: 56, borderRadius: 16, background: 'rgba(123,13,30,0.06)', color: BRAND, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                    <Search size={24} />
                  </div>
                  <p style={{ fontSize: 15, fontWeight: 600, color: '#1f2937', margin: 0 }}>{L.noResults}</p>
                  <p style={{ fontSize: 13, color: '#9ca3af', margin: '6px 0 0' }}>
                    {L.noMatch(query)}
                  </p>
                </div>
              ) : (
                <>
                  {Object.entries(DB_CATEGORY_CONFIG).map(([type, cfg]) => {
                    const items = dbResults[type];
                    if (!items || items.length === 0) return null;
                    return (
                      <div key={type}>
                        {sectionTitle(isEn ? cfg.en : cfg.label, cfg.color)}
                        {items.map(item => {
                          const idx   = indexOf(`${type}-${item.id}`);
                          const title = item.baslik || item.title || '';
                          const sub   = item.ozet || item.summary || item.yazarlar || item.konum || '';
                          const date  = item.tarih || item.date || '';
                          return (
                            <button key={item.id} type="button" data-idx={idx}
                              onMouseEnter={() => setActive(idx)}
                              onClick={() => go(getResultLink(type, item))}
                              style={rowStyle(active === idx)}>
                              {iconTile(cfg.icon, cfg.color)}
                              <span style={{ flex: 1, minWidth: 0 }}>
                                <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: active === idx ? BRAND : '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {highlight(title, query)}
                                </span>
                                {sub && (
                                  <span style={{ display: 'block', fontSize: 12, color: '#6b7280', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {highlight(sub, query)}
                                  </span>
                                )}
                              </span>
                              {date && <span style={{ fontSize: 11, color: '#9ca3af', flexShrink: 0 }}>{date}</span>}
                            </button>
                          );
                        })}
                      </div>
                    );
                  })}

                  {pages.length > 0 && (
                    <div>
                      {sectionTitle(L.pages)}
                      {pages.map(page => {
                        const idx = indexOf(`p-${page.url}`);
                        return (
                          <button key={page.url} type="button" data-idx={idx}
                            onMouseEnter={() => setActive(idx)}
                            onClick={() => go(pageLink(page))}
                            style={rowStyle(active === idx)}>
                            {iconTile(FileText, '#4b5563')}
                            <span style={{ flex: 1, minWidth: 0 }}>
                              <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: active === idx ? BRAND : '#1f2937' }}>
                                {highlight(page.title, query)}
                              </span>
                              <span style={{ display: 'block', fontSize: 12, color: '#6b7280', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {page.description}
                              </span>
                            </span>
                            <span style={{ fontSize: 11, fontWeight: 600, background: '#f3f4f6', color: '#6b7280', padding: '3px 10px', borderRadius: 999, flexShrink: 0 }}>
                              {page.category}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Alt bilgi */}
            <div className="site-search-footer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 20px', borderTop: '1px solid #f1f1f3', background: '#fafafa', fontSize: 12, color: '#9ca3af', flexShrink: 0 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Kbd>↑</Kbd><Kbd>↓</Kbd> {L.nav}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Kbd><CornerDownLeft size={11} style={{ verticalAlign: '-1px' }} /></Kbd> {L.open}</span>
              </span>
              <span>
                {hasQ && !loading
                  ? <><b style={{ color: '#4b5563' }}>{total}</b> {L.results}</>
                  : L.footer}
              </span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(content, document.body);
}
